import { supabase, requireSession } from "@/lib/supabase/client";
import type { Json } from "@/lib/supabase/database.types";
import type { WeeklyCheckIn } from "@/types";

/**
 * Check-In Service — reads and writes WeeklyCheckIn records to Supabase.
 *
 * Each check-in is keyed by (user_id, week_start_date) — there is at most one
 * check-in per user per week and saving again updates it in place.
 */
export const checkInService = {
  /**
   * Load all check-ins for the current user.
   */
  async loadAll(userId: string): Promise<WeeklyCheckIn[]> {
    const { data, error } = await supabase
      .from("check_ins")
      .select("data")
      .eq("user_id", userId)
      .order("week_start_date", { ascending: false });

    if (error) {
      console.error(
        `[checkInService.loadAll] failed (code: ${error.code}): ${error.message}`,
      );
      return [];
    }

    return (data ?? []).map((row) => row.data as unknown as WeeklyCheckIn);
  },

  /**
   * Upsert a single check-in. Updating overwrites the previous value for the
   * same week (the unique constraint on user_id + week_start_date handles it).
   */
  async save(userId: string, checkIn: WeeklyCheckIn): Promise<void> {
    await requireSession(userId);
    const { error } = await supabase.from("check_ins").upsert(
      {
        user_id: userId,
        week_start_date: checkIn.weekStartDate,
        data: checkIn as unknown as Json,
      },
      { onConflict: "user_id,week_start_date" },
    );

    if (error)
      throw new Error(
        `[checkInService.save] ${error.message} (code: ${error.code})${
          error.hint ? ` — ${error.hint}` : ""
        }`,
      );
  },

  /**
   * Bulk-insert check-ins (used by migration utility).
   */
  async bulkSave(userId: string, checkIns: WeeklyCheckIn[]): Promise<void> {
    if (checkIns.length === 0) return;

    const rows = checkIns.map((c) => ({
      user_id: userId,
      week_start_date: c.weekStartDate,
      data: c as unknown as Json,
    }));

    const { error } = await supabase
      .from("check_ins")
      .upsert(rows, { onConflict: "user_id,week_start_date" });

    if (error) throw new Error(`[checkInService.bulkSave] ${error.message}`);
  },
};
